import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { ProjectView } from '@lemoncloud/codes-goods-api';
import { DangerZone, EditableField, SectionHeader } from '@web/app/features/home/components/settings';
import { useProductService, useProjectService } from '@web/app/features/home/hooks';
import { GuardScope, useAccessGuard, useSelectedStore, useUpdateQueryData } from '@web/app/shared';
import { AlertTriangle, RefreshCw } from 'lucide-react';

import { projectsKeys, useDeleteProject, useUpdateProject } from '@codes/project';
import { PaginationType } from '@codes/shared';
import { Button, Skeleton } from '@codes/ui-kit';

export const ProjectSettingsPage = () => {
    useAccessGuard(GuardScope.Workspace, true);

    const { setProject, workspace, project } = useSelectedStore();
    const { myProducts } = useProductService();
    const { myProjectsQueryResult } = useProjectService();

    const updateProjectQueryData = useUpdateQueryData<PaginationType<ProjectView[]>>();
    const { t } = useTranslation();

    const { mutateAsync: deleteProject, isPending: isDeleting } = useDeleteProject();
    const { mutateAsync: updateProject } = useUpdateProject();

    const deleteButtonTapped = async () => {
        if (!project || !workspace || !project.id || !workspace.id) {
            return;
        }
        const isOkay = confirm(t('workspace.setting.confirmToDeleteProject'));

        if (!isOkay) {
            return;
        }

        const deletedProject = await deleteProject(project.id);

        setProject(null);
        toast({ description: t('workspace.setting.completeToDeleteProject') });

        const queryKey = projectsKeys.list({ workspaceId: workspace.id, params: { limit: 50, page: 0 } });

        updateProjectQueryData({
            queryKey,
            updater: oldData => {
                if (!oldData) {
                    return { total: 0, data: [], list: [], page: 0 };
                }
                const updatedData = oldData.data.filter(oldWorkspace => {
                    return oldWorkspace.id !== deletedProject.id;
                });
                return {
                    ...oldData,
                    data: updatedData,
                    list: updatedData,
                    total: (oldData.total || 1) - 1,
                };
            },
        });
    };

    const productsNameLabel = useMemo(() => {
        if (!myProducts || myProducts.length === 0) {
            return null;
        }

        const product = myProducts[0];
        const productsCount = myProducts.length;

        if (productsCount === 1) {
            return <div className="text-base text-gray-700 font-medium">{product?.name}</div>;
        }

        return (
            <div className="text-base text-gray-700 font-medium">
                {t('project.serviceCountLabel', {
                    serviceName: product.name,
                    count: (productsCount || 1) - 1,
                })}
            </div>
        );
    }, [myProducts]);

    if (myProjectsQueryResult.isFetching || myProjectsQueryResult.isRefetching) {
        return (
            <div className="flex flex-col py-4 px-4 md:py-12 md:px-16 w-full items-center">
                <div className="w-full max-w-[720px] space-y-6 md:space-y-8">
                    <div className="mb-6">
                        <Skeleton className="h-8 w-48 mb-2" />
                    </div>
                    <div className="space-y-4">
                        <Skeleton className="h-6 w-32 mb-3" />
                        <Skeleton className="h-24 w-full rounded-xl" />
                    </div>
                    <div className="space-y-4">
                        <Skeleton className="h-6 w-24 mb-3" />
                        <Skeleton className="h-32 w-full rounded-xl" />
                    </div>
                </div>
            </div>
        );
    }

    if (myProjectsQueryResult.isError) {
        return (
            <div className="flex flex-col py-4 px-4 md:py-12 md:px-16 w-full items-center">
                <div className="w-full max-w-[720px]">
                    <div className="mb-6">
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                            {t('project.setting.title')}
                        </h1>
                    </div>
                    <div className="flex flex-col items-center gap-6 py-12">
                        <div className="w-16 h-16 rounded-full bg-error/15 flex items-center justify-center">
                            <AlertTriangle className="w-8 h-8 text-error" />
                        </div>
                        <div className="text-center">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                {t('project.error.fetchDetail')}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">{t('common.retry')}</p>
                        </div>
                        <Button
                            onClick={() => myProjectsQueryResult.refetch()}
                            disabled={myProjectsQueryResult.isRefetching}
                            className="gap-2"
                        >
                            <RefreshCw
                                className={`w-4 h-4 ${myProjectsQueryResult.isRefetching ? 'animate-spin' : ''}`}
                            />
                            {t('project.error.retry')}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="flex flex-col py-4 px-4 md:py-12 md:px-16 w-full items-center">
                <div className="w-full max-w-[720px]">
                    <div className="mb-6">
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                            {t('project.setting.title')}
                        </h1>
                    </div>
                    <div className="flex flex-col items-center gap-6 py-12">
                        <div className="text-center">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                프로젝트를 선택해주세요
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">설정할 프로젝트를 먼저 선택해주세요.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col py-4 px-4 md:py-12 md:px-16 w-full items-center">
            <div className="w-full max-w-[720px] space-y-6 md:space-y-8">
                {/* Page Header */}
                <div className="mb-6">
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                        {t('project.setting.title')}
                    </h1>
                </div>

                {/* General Settings Section */}
                <div className="space-y-4">
                    <SectionHeader title={t('workspace.setting.generalSettings') || 'General Settings'} />

                    <EditableField
                        label={t('project.setting.labelText')}
                        value={project?.name || ''}
                        onSave={async value => {
                            if (!project || !project.id || !workspace || !workspace.id) {
                                return;
                            }

                            const updatedProject = await updateProject({ projectId: project.id, name: value });

                            setProject(updatedProject);
                            toast({ description: t('project.edited') });

                            const queryKey = projectsKeys.list({
                                workspaceId: workspace.id,
                                params: { limit: 50, page: 0 },
                            });

                            updateProjectQueryData({
                                queryKey,
                                updater: oldData => {
                                    if (!oldData) {
                                        return { total: 0, data: [], list: [], page: 0 };
                                    }
                                    const updatedData = oldData.data.map(oldProject => {
                                        if (oldProject.id === updatedProject.id) {
                                            return updatedProject;
                                        }
                                        return oldProject;
                                    });
                                    return {
                                        ...oldData,
                                        data: updatedData,
                                        list: updatedData,
                                    };
                                },
                            });
                        }}
                        placeholder={t('project.setting.labelText')}
                    />
                </div>

                {/* Danger Zone Section */}
                <div className="space-y-4">
                    <DangerZone
                        title={t('workspace.setting.projectDeletionLabelText')}
                        warning={t('workspace.setting.projectDeletionContentText')}
                        entityName={project?.name || ''}
                        entityInfo={productsNameLabel}
                        onDelete={deleteButtonTapped}
                        deleteButtonText={t('project.delete')}
                        isDeleting={isDeleting}
                    />
                </div>
            </div>
        </div>
    );
};
